import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/shared/ThemeToggle';
import { getApiUrl } from '@/config/api';
import { safeJsonParse } from '@/utils/apiUtils';

/**
 * Password Reset Confirm Component
 * 
 * Allows users to set a new password using the token from the reset email.
 * Features comprehensive validation, token verification, and error handling.
 * 
 * Flow:
 * 1. Component mounts and validates reset token
 * 2. If valid, shows password form
 * 3. User enters and confirms new password
 * 4. Password is updated and user redirected to login
 * 5. If invalid/expired token, shows error with option to request new link
 */
const PasswordResetConfirm: React.FC = () => {
    const { uid, token } = useParams<{ uid: string; token: string }>();
    const navigate = useNavigate();
    const { isDark } = useTheme();
    
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [validating, setValidating] = useState<boolean>(true);
    const [valid, setValid] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<boolean>(false);
    const [maskedEmail, setMaskedEmail] = useState<string>('');

    /**
     * Validate token on component mount
     * 
     * Checks if the reset link is valid before showing the password form.
     * This provides immediate feedback if the link is expired or invalid.
     */
    useEffect(() => {
        const validateToken = async () => {
            if (!uid || !token) {
                setError('Invalid reset link');
                setValidating(false);
                setValid(false);
                return;
            }

            try {
                const response = await fetch(getApiUrl('/api/password-reset/validate/'), {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ uid, token }),
                });

                const result = await safeJsonParse<{ valid: boolean; email?: string }>(response, 'validating reset token');
                
                if (result.success && result.data!.valid) {
                    setValid(true);
                    setMaskedEmail(result.data!.email || '');
                } else {
                    setValid(false);
                    setError(result.error || 'Invalid or expired reset link');
                }
            } catch (err) {
                setValid(false);
                setError('Failed to validate reset link. Please try again.');
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [uid, token]);

    /**
     * Handle password reset form submission
     * 
     * Validates passwords match and meet requirements, then submits to backend.
     * On success, redirects to login page after brief delay.
     */
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        // Client-side validation
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(getApiUrl('/api/password-reset/confirm/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uid,
                    token,
                    new_password: password,
                }),
            });

            const result = await safeJsonParse(response, 'password reset confirmation');
            
            if (result.success) {
                setSuccess(true);
                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setError(result.error || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Loading screen while validating token
    if (validating) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>
                
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 mx-auto cgm-spinner border-4 mb-4"></div>
                    <p className="cgm-text-secondary">Validating reset link...</p>
                </div>
            </div>
        );
    }

    // Invalid token screen
    if (!valid) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>

                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <img
                            className="mx-auto h-24 w-auto mb-8"
                            src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                            alt="Wrestling Team Logo"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = isDark
                                    ? "/logos/LOGO-MAIN-WHITE.png"
                                    : "/logos/LOGO-MAIN-BLK.png";
                            }}
                        />
                        <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                            Invalid Reset Link
                        </h2>
                    </div>

                    <div className="cgm-card p-8">
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-6 text-sm">
                            {error}
                        </div>

                        <div className="space-y-4 cgm-text-secondary text-sm mb-8">
                            <p>
                                This password reset link is invalid or has expired. Reset links are only valid for 24 hours.
                            </p>
                            <p>
                                Common reasons:
                            </p>
                            <ul className="list-disc list-inside space-y-1 pl-2">
                                <li>Link has expired (24 hours have passed)</li>
                                <li>Link has already been used</li>
                                <li>Link was copied incorrectly</li>
                            </ul>
                        </div>

                        <div className="space-y-3">
                            <button
                                onClick={() => navigate('/password-reset')}
                                className="cgm-button w-full py-3 px-4"
                            >
                                Request New Reset Link
                            </button>
                            
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-3 px-4 cgm-text-secondary hover:cgm-text-primary transition-colors duration-200"
                            >
                                Return to Login
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Success screen after password reset
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
                <div className="fixed top-4 right-4 z-50">
                    <ThemeToggle />
                </div>

                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <img
                            className="mx-auto h-24 w-auto mb-8"
                            src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                            alt="Wrestling Team Logo"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = isDark
                                    ? "/logos/LOGO-MAIN-WHITE.png"
                                    : "/logos/LOGO-MAIN-BLK.png";
                            }}
                        />
                        <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                            Password Reset Successful
                        </h2>
                    </div>

                    <div className="cgm-card p-8">
                        <div className="cgm-alert-success px-4 py-3 rounded-md mb-6 text-sm">
                            ✓ Your password has been reset successfully
                        </div>

                        <div className="space-y-4 cgm-text-secondary text-sm mb-8">
                            <p>
                                You can now log in with your new password.
                            </p>
                            <p className="text-xs cgm-text-muted">
                                Redirecting to login page in 3 seconds...
                            </p>
                        </div>

                        <button
                            onClick={() => navigate('/login')}
                            className="cgm-button w-full py-3 px-4"
                        >
                            Go to Login Now
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Password reset form screen
    return (
        <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 cgm-bg-primary transition-colors duration-300">
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <img
                        className="mx-auto h-24 w-auto mb-8 transition-all duration-200 hover:scale-110 cursor-pointer"
                        src={isDark ? "/logos/LOGO-MAIN-WHITE.svg" : "/logos/LOGO-MAIN-BLK.svg"}
                        alt="Wrestling Team Logo"
                        onClick={() => navigate('/')}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = isDark
                                ? "/logos/LOGO-MAIN-WHITE.png"
                                : "/logos/LOGO-MAIN-BLK.png";
                        }}
                    />
                    <h2 className="text-3xl font-light tracking-tight cgm-text-primary mb-2">
                        Set New Password
                    </h2>
                    {maskedEmail && (
                        <p className="cgm-text-secondary text-sm">
                            Resetting password for {maskedEmail}
                        </p>
                    )}
                </div>

                <div className="cgm-card p-8">
                    {error && (
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-6 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-2">
                                New Password
                            </label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                className="cgm-input w-full px-4 py-3"
                                placeholder="At least 8 characters"
                                minLength={8}
                                autoFocus
                            />
                            <p className="mt-2 text-xs cgm-text-muted">
                                Must be at least 8 characters long
                            </p>
                        </div>

                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium cgm-text-secondary mb-2">
                                Confirm Password
                            </label>
                            <input
                                id="confirmPassword"
                                name="confirmPassword"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={loading}
                                className="cgm-input w-full px-4 py-3"
                                placeholder="Re-enter your password"
                                minLength={8}
                            />
                            {password && confirmPassword && password !== confirmPassword && (
                                <p className="mt-2 text-xs text-red-500">
                                    Passwords do not match
                                </p>
                            )}
                            {password && confirmPassword && password === confirmPassword && (
                                <p className="mt-2 text-xs text-green-500">
                                    ✓ Passwords match
                                </p>
                            )}
                        </div>

                        <button
                            type="submit"
                            disabled={loading || password !== confirmPassword || password.length < 8}
                            className="cgm-button w-full mt-8 py-3 px-4 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                    Resetting Password...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="cgm-text-muted text-sm">
                            Remember your password?{' '}
                            <Link
                                to="/login"
                                className="cgm-text-primary hover:cgm-text-secondary font-medium transition-colors duration-200 underline underline-offset-4"
                            >
                                Back to Login
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordResetConfirm;

